import { ModuleWithProviders, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';

import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { OAuthModule, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';

import { SharedAuthenticationUiModule } from '@cash/shared/authentication-ui';

import { AuthenticationEffects } from './state/authentication.effects';
import { AuthenticationFacade } from './state';
import { CashOAuthService } from './services/cash-oauth.service';
import { AuthenticatedGuard } from './services/authenticated.guard';
import { AuthCallbackComponent } from './components';
import { routes } from './shared-authentication-routing.module';
import * as fromAuthentication from './state/authentication.reducer';
import { AccessDeniedInterceptor } from './interceptor/access-denied-interceptor.service';
import { OAuthInterceptor } from './interceptor/oauth-interceptor';
import { storageFactory } from './constants';

export class SharedAuthenticationModuleConfig {
  allowedUrls?: Array<string>;
  clientId: string;
  loginUrl: string;
  redirectUri: string;
  tokenEndpoint: string;
}

const components = [AuthCallbackComponent];

@NgModule({
  imports: [
    CommonModule,
    StoreModule.forFeature(
      fromAuthentication.AUTHENTICATION_FEATURE_KEY,
      fromAuthentication.reducer,
    ),
    EffectsModule.forFeature([AuthenticationEffects]),
    SharedAuthenticationUiModule,
  ],
  declarations: [...components],
  exports: [...components],
})
export class SharedAuthenticationModuleForChild {}

@NgModule({
  imports: [
    CommonModule,
    StoreModule.forFeature(
      fromAuthentication.AUTHENTICATION_FEATURE_KEY,
      fromAuthentication.reducer,
    ),
    EffectsModule.forFeature([AuthenticationEffects]),
    RouterModule.forChild(routes),
  ],
})
export class SharedAuthenticationModuleForRoot {
  constructor(
    private config: SharedAuthenticationModuleConfig,
    private authenticationFacade: AuthenticationFacade,
  ) {
    this.authenticationFacade.configure({
      ...this.config,
      responseType: 'code',
      timeoutFactor: 0.5,
      oidc: true,
      scope: 'openid offline_access',
      disablePKCE: true,
      waitForTokenInMsec: 1000,
    });
    this.authenticationFacade.setupAutomaticSilentRefresh();

    this.authenticationFacade.events.subscribe((oauthEvent) => {
      if (
        oauthEvent.type === 'token_received' ||
        (oauthEvent.type === 'token_refreshed' && this.authenticationFacade.hasValidAccessToken())
      ) {
        this.authenticationFacade.accessTokenReceived();
      }
    });
  }
}

export class SharedAuthenticationModule {
  static forRoot(
    config: SharedAuthenticationModuleConfig,
  ): ModuleWithProviders<SharedAuthenticationModuleForRoot> {
    const oAuthModule = OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: config.allowedUrls,
        sendAccessToken: true,
      },
    });
    return {
      ngModule: SharedAuthenticationModuleForRoot,
      providers: [
        ...oAuthModule.providers,
        AuthenticationFacade,
        AuthenticatedGuard,
        {
          provide: OAuthService,
          useClass: CashOAuthService,
        },
        {
          provide: SharedAuthenticationModuleConfig,
          useValue: config,
        },
        OAuthInterceptor,
        {
          provide: HTTP_INTERCEPTORS,
          useClass: OAuthInterceptor,
          multi: true,
        },
        AccessDeniedInterceptor,
        {
          provide: HTTP_INTERCEPTORS,
          useClass: AccessDeniedInterceptor,
          multi: true,
        },
        {
          provide: OAuthStorage,
          useFactory: storageFactory,
        },
      ],
    };
  }

  static forChild(): ModuleWithProviders<SharedAuthenticationModuleForChild> {
    return {
      ngModule: SharedAuthenticationModuleForChild,
    };
  }
}
